import { Job, JobStatus, SupabaseJob } from '@applicator/shared'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { JobDetailContent } from './JobDetail/components/JobDetailContent'
import { JobsListColumn } from './JobDetail/components/JobsListColumn'
import { pipelineStatuses, closeJobStatuses } from './JobDetail/constants'
import { handlePositionEditStart, handlePositionSave, handlePositionKeyDown, handleCompanyEditStart, handleCompanySave as handleCompanySaveFromHandlers, handleCompanyKeyDown as handleCompanyKeyDownFromHandlers, handleCompanyCancel as handleCompanyCancelFromHandlers, handleSalaryEditStart, handleSalarySave, handleSalaryKeyDown, handleSalaryInputChange, handleExcitementChange } from './JobDetail/editingHandlers'
import { useInitialMountAnimationEffect } from './JobDetail/effects/animation'
import { useClickOutsideEffect } from './JobDetail/effects/clickOutside'
import { useErrorToastEffect } from './JobDetail/effects/errorToast'
import { setupLoadingTimeout, setSelectedJobFromMapped } from './JobDetail/effects/helpers'
import { useLazyLoadingEffect, useAllJobsLoadedEffect } from './JobDetail/effects/lazyLoading'
import { initializeLoadingState, loadJobsSuccessfully, handleLoadingError } from './JobDetail/effects/loading'
import { useRealtimeSubscriptionEffect } from './JobDetail/effects/realtimeSubscription'
import { setupScrollbarVisibility } from './JobDetail/effects/scroll'
import { filterBySearchQuery, filterByRelevance, RelevanceFilter } from './JobDetail/filters'
import { createHandleDateChipsUpdateWrapper, handleStatusChange, handleAddToJobs, handleRejectRelevant, handleDeleteJob, handleJobSelect } from './JobDetail/handlers'
import { setupSavedAtTooltipListeners } from './JobDetail/savedAtTooltip'
import { loadUserSettings } from './JobDetail/userSettings'


function JobDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const searchQueryId = searchParams.get('searchQueryId')
  const [jobs, setJobs] = useState<Job[]>([])
  const [supabaseJobs, setSupabaseJobs] = useState<SupabaseJob[]>([])
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [displayedJobsCount, setDisplayedJobsCount] = useState(20) // Lazy loading: show first 20
  const [allJobsLoaded, setAllJobsLoaded] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null)
  const [jobSearchQuery, setJobSearchQuery] = useState<string>('') // Search query for jobs list
  const [relevanceFilter, setRelevanceFilter] = useState<RelevanceFilter>('all') // Relevance filter
  const [userRules, setUserRules] = useState<{ follow_up_days_after_applied?: number | null; deadline_days_after_saved?: number | null }>({
    follow_up_days_after_applied: 7,
    deadline_days_after_saved: 14,
  })
  // Refs for job list items to enable scrolling to selected job
  const jobItemRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  // State for collapsible relevance reasoning
  const [showRelevanceReasoning, setShowRelevanceReasoning] = useState(false)

  // Load user settings from Supabase
  useEffect(() => {
    loadUserSettings(setUserRules)
  }, [])
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isInitialMount, setIsInitialMount] = useState(true)
  const [isReturning, setIsReturning] = useState(false)
  const [previousId, setPreviousId] = useState<string | undefined>(id)
  const [editingPosition, setEditingPosition] = useState(false)
  const [editingCompany, setEditingCompany] = useState(false)
  const [editingSalary, setEditingSalary] = useState(false)
  const [positionInputValue, setPositionInputValue] = useState<string>('')
  const [companyInputValue, setCompanyInputValue] = useState<string>('')
  const [salaryInputValue, setSalaryInputValue] = useState<string>('')
  const [hoveredStar, setHoveredStar] = useState<number | null>(null)
  const [closeJobDropdownOpen, setCloseJobDropdownOpen] = useState(false)
  const [showSavedAtTooltip, setShowSavedAtTooltip] = useState(false)
  const [savedAtTooltipPos, setSavedAtTooltipPos] = useState<{ top: number; left: number } | null>(null)
  const [savedAtTooltipText, setSavedAtTooltipText] = useState<string>('')
  const savedAtRef = useRef<HTMLSpanElement>(null)
  const detailContentRef = useRef<HTMLDivElement>(null)
  const leftColumnRef = useRef<HTMLDivElement>(null)
  const jobsListScrollRef = useRef<HTMLDivElement>(null)
  const jobDetailScrollRef = useRef<HTMLDivElement>(null)
  const positionInputRef = useRef<HTMLInputElement>(null)
  const companyInputRef = useRef<HTMLInputElement>(null)
  const salaryInputRef = useRef<HTMLInputElement>(null)
  const closeJobDropdownRef = useRef<HTMLDivElement>(null)
  const closeJobButtonRef = useRef<HTMLButtonElement>(null)

  // Hide savedAt tooltip on scroll, blur, or click outside
  useEffect(() => {
    return setupSavedAtTooltipListeners(savedAtRef, setShowSavedAtTooltip, setSavedAtTooltipPos)
  }, [])


  // Reset relevance reasoning visibility when job changes
  useEffect(() => {
    setShowRelevanceReasoning(false)
  }, [id])

  // Helper function to check if id changed and should trigger animation
  const shouldTriggerAnimation = (currentId: string | undefined, prevId: string | undefined): boolean => {
    return !!(currentId && currentId !== prevId && prevId !== undefined)
  }

  // Helper function to handle id change animation
  const handleIdChangeAnimation = () => {
    setIsInitialMount(true)
    setLoading(false)
  }

  // Отслеживаем изменение id для запуска анимации при переходе со списка
  useEffect(() => {
    // Если id изменился и это не первая загрузка, запускаем анимацию
    if (shouldTriggerAnimation(id, previousId)) {
      handleIdChangeAnimation()
    }
    if (id) {
      setPreviousId(id)
    }
  }, [id, previousId])


  // Загружаем все вакансии (но отображаем только первые 20)
  useEffect(() => {
    const loadJobs = async () => {
      const isFirstLoad = jobs.length === 0
      initializeLoadingState(
        isFirstLoad,
        setLoading,
        setError,
        setDisplayedJobsCount,
        setAllJobsLoaded
      )
      const loadingTimeout = setupLoadingTimeout(setError, setLoading)

      // Create wrapper function for setSelectedJobFromMapped
      const setSelectedJobFromMappedWrapper = (mappedJobs: Job[]) => {
        setSelectedJobFromMapped(mappedJobs, id, setSelectedJobId)
      }

      try {
        await loadJobsSuccessfully(
          loadingTimeout,
          setJobs,
          setSupabaseJobs,
          setAllJobsLoaded,
          setSelectedJobFromMappedWrapper
        )
      } catch (err: unknown) {
        handleLoadingError(err, loadingTimeout, setError)
      } finally {
        clearTimeout(loadingTimeout)
        setLoading(false)
      }
    }

    loadJobs()
  }, [id]) // Убрали isInitialMount из зависимостей


  // Filter jobs based on search query and relevance
  const filteredJobs = useMemo(() => {
    let result = filterBySearchQuery(jobs, jobSearchQuery)
    result = filterByRelevance(result, relevanceFilter, supabaseJobs)
    return result
  }, [jobs, jobSearchQuery, relevanceFilter, supabaseJobs])

  // Use effects from modules
  useAllJobsLoadedEffect(filteredJobs.length, displayedJobsCount, setAllJobsLoaded)
  useLazyLoadingEffect(allJobsLoaded, isLoadingMore, filteredJobs.length, setDisplayedJobsCount, setAllJobsLoaded, setIsLoadingMore, loadMoreTriggerRef)
  useRealtimeSubscriptionEffect(selectedJobId, setSupabaseJobs, setJobs, supabaseJobs)
  useInitialMountAnimationEffect(isInitialMount, leftColumnRef, detailContentRef, setIsInitialMount, setLoading)
  useClickOutsideEffect(closeJobDropdownOpen, closeJobDropdownRef, closeJobButtonRef, setCloseJobDropdownOpen)
  useErrorToastEffect(error)

  // Scroll to selected job when selectedJobId changes
  useEffect(() => {
    if (!selectedJobId || !jobsListScrollRef.current) return

    // Wait for DOM to update and ensure the item is rendered
    const timeoutId = setTimeout(() => {
      const selectedItemRef = jobItemRefs.current.get(selectedJobId)
      if (selectedItemRef && jobsListScrollRef.current) {
        // Get the scroll container
        const scrollContainer = jobsListScrollRef.current
        // Get the position of the selected item relative to the scroll container
        const itemRect = selectedItemRef.getBoundingClientRect()
        const containerRect = scrollContainer.getBoundingClientRect()
        
        // Calculate the scroll position needed to place the item at the top
        const scrollTop = scrollContainer.scrollTop + (itemRect.top - containerRect.top)
        
        // Scroll smoothly to position the item at the top
        scrollContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        })
      }
    }, 150) // Small delay to ensure DOM is updated and item is rendered

    return () => clearTimeout(timeoutId)
  }, [selectedJobId, filteredJobs, displayedJobsCount])

  // Show scrollbar on scroll for jobs list
  useEffect(() => {
    return setupScrollbarVisibility(jobsListScrollRef.current, 'scrolling')
  }, [])

  // Show scrollbar on scroll for job detail
  useEffect(() => {
    return setupScrollbarVisibility(jobDetailScrollRef.current, 'scrolling')
  }, [])

  // Early return if no id - this should not happen but protects against routing issues
  if (!id) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-danger">Error: No job ID provided</div>
      </div>
    )
  }

  const selectedJob = selectedJobId !== null ? jobs.find(j => j.id === selectedJobId) : null

  // Early return if still loading or jobs list is empty (still loading)
  if (loading || jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-text-muted">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-danger font-medium mb-2">Error loading job</div>
          <div className="text-sm text-text-secondary mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-brand-primary text-text-inverse rounded-md hover:bg-brand-primary-hover text-sm"
          >
            Reload Page
          </button>
        </div>
      </div>
    )
  }

  // Only show "Job not found" if jobs are loaded but selected job is not found
  // This should only happen if the job ID in URL is invalid
  if (!selectedJob && jobs.length > 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-text-muted">Job not found</div>
      </div>
    )
  }

  // If no job selected but jobs are available, show loading while we wait for selection
  if (!selectedJob) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-text-muted">Loading...</div>
      </div>
    )
  }

  // TypeScript guard: after early return, selectedJob is guaranteed to be non-null
  // But we still need to be careful in JSX conditional rendering
  const currentJob = selectedJob



  // Wrapper for handleDateChipsUpdate
  const handleDateChipsUpdateWrapper = createHandleDateChipsUpdateWrapper(currentJob, supabaseJobs, setSupabaseJobs, jobs, setJobs)

  // Wrapper functions for editing handlers
  const handlePositionEditStartWrapper = () => {
    handlePositionEditStart(currentJob, setPositionInputValue, setEditingPosition, positionInputRef)
  }

  const handlePositionSaveWrapper = () => {
    handlePositionSave(currentJob, positionInputValue, jobs, setJobs, setEditingPosition, setError)
  }

  const handlePositionKeyDownWrapper = (e: React.KeyboardEvent) => {
    handlePositionKeyDown(e, handlePositionSaveWrapper, handlePositionCancel)
  }

  const handlePositionCancel = () => {
    setEditingPosition(false)
    setPositionInputValue('')
  }

  const handleCompanyEditStartWrapper = () => {
    handleCompanyEditStart(currentJob, setCompanyInputValue, setEditingCompany, companyInputRef)
  }

  const handleCompanySaveWrapper = async () => {
    await handleCompanySaveFromHandlers(currentJob, companyInputValue, jobs, setJobs, setEditingCompany, setError)
  }

  const handleCompanyCancelWrapper = () => {
    handleCompanyCancelFromHandlers(currentJob, setEditingCompany, setCompanyInputValue)
  }

  const handleCompanyKeyDownWrapper = (e: React.KeyboardEvent) => {
    handleCompanyKeyDownFromHandlers(e, handleCompanySaveWrapper, handleCompanyCancelWrapper)
  }

  const handleSalaryEditStartWrapper = () => {
    handleSalaryEditStart(currentJob, setSalaryInputValue, setEditingSalary, salaryInputRef)
  }

  const handleSalarySaveWrapper = () => {
    handleSalarySave(currentJob, salaryInputValue, jobs, setJobs, setEditingSalary, setError)
  }

  const handleSalaryKeyDownWrapper = (e: React.KeyboardEvent) => {
    handleSalaryKeyDown(e, handleSalarySaveWrapper, handleSalaryCancel)
  }

  const handleSalaryCancel = () => {
    setEditingSalary(false)
    setSalaryInputValue('')
  }

  const handleSalaryInputChangeWrapper = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSalaryInputChange(e, setSalaryInputValue)
  }

  const handleExcitementChangeWrapper = (value: number) => {
    handleExcitementChange(currentJob, value, jobs, setJobs, setError)
  }

  // Wrapper functions for handlers
  const handleStatusChangeWrapper = async (newStatus: JobStatus) => {
    await handleStatusChange(currentJob, newStatus, jobs, setJobs, supabaseJobs, setSupabaseJobs, setCloseJobDropdownOpen, setError)
  }

  const handleAddToJobsWrapper = async () => {
    await handleAddToJobs(currentJob, searchQueryId, jobs, setJobs, supabaseJobs, setSupabaseJobs, jobSearchQuery, navigate, setError)
  }

  const handleRejectRelevantWrapper = async () => {
    await handleRejectRelevant(currentJob, searchQueryId, jobs, setJobs, supabaseJobs, setSupabaseJobs, jobSearchQuery, navigate, setError)
  }

  const handleDeleteJobWrapper = async () => {
    await handleDeleteJob(currentJob, searchQueryId, jobs, setJobs, setCloseJobDropdownOpen, navigate, setError)
  }

  const handleJobSelectWrapper = (jobId: string) => {
    handleJobSelect(jobId, selectedJobId, searchQueryId, setSelectedJobId, setEditingPosition, setEditingCompany, setEditingSalary, setCloseJobDropdownOpen, setIsTransitioning, detailContentRef)
  }



  return (
    <div className="flex flex-col h-full w-full" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="flex flex-1" style={{ height: '100%', width: '100%', minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          <JobsListColumn
            leftColumnRef={leftColumnRef}
            isInitialMount={isInitialMount}
            isReturning={isReturning}
            jobSearchQuery={jobSearchQuery}
            setJobSearchQuery={setJobSearchQuery}
            setDisplayedJobsCount={setDisplayedJobsCount}
            setAllJobsLoaded={setAllJobsLoaded}
            relevanceFilter={relevanceFilter}
            setRelevanceFilter={setRelevanceFilter}
            jobsListScrollRef={jobsListScrollRef}
            loading={loading}
            error={error}
            filteredJobs={filteredJobs}
            displayedJobsCount={displayedJobsCount}
            allJobsLoaded={allJobsLoaded}
            isLoadingMore={isLoadingMore}
            loadMoreTriggerRef={loadMoreTriggerRef}
            supabaseJobs={supabaseJobs}
            selectedJobId={selectedJobId}
            handleJobSelect={handleJobSelectWrapper}
            jobItemRefs={jobItemRefs}
          />

          {/* Right Column - 3/4 width - Job Details */}
          <div 
            className="flex-1 bg-surface-default relative flex flex-col" 
            style={{ 
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {currentJob ? (
              <JobDetailContent
                currentJob={currentJob}
                detailContentRef={detailContentRef}
                jobDetailScrollRef={jobDetailScrollRef}
                isInitialMount={isInitialMount}
                isReturning={isReturning}
                isTransitioning={isTransitioning}
                searchQueryId={searchQueryId}
                navigate={navigate}
                setIsReturning={setIsReturning}
                leftColumnRef={leftColumnRef}
                editingPosition={editingPosition}
                editingCompany={editingCompany}
                editingSalary={editingSalary}
                positionInputValue={positionInputValue}
                companyInputValue={companyInputValue}
                salaryInputValue={salaryInputValue}
                positionInputRef={positionInputRef}
                companyInputRef={companyInputRef}
                salaryInputRef={salaryInputRef}
                savedAtRef={savedAtRef}
                setSavedAtTooltipPos={setSavedAtTooltipPos}
                setSavedAtTooltipText={setSavedAtTooltipText}
                setShowSavedAtTooltip={setShowSavedAtTooltip}
                hoveredStar={hoveredStar}
                setHoveredStar={setHoveredStar}
                supabaseJobs={supabaseJobs}
                showRelevanceReasoning={showRelevanceReasoning}
                setShowRelevanceReasoning={setShowRelevanceReasoning}
                userRules={userRules}
                handlePositionEditStartWrapper={handlePositionEditStartWrapper}
                handlePositionSaveWrapper={handlePositionSaveWrapper}
                handlePositionKeyDownWrapper={handlePositionKeyDownWrapper}
                handleCompanyEditStartWrapper={handleCompanyEditStartWrapper}
                handleCompanySave={handleCompanySaveWrapper}
                handleCompanyKeyDown={handleCompanyKeyDownWrapper}
                handleSalaryEditStartWrapper={handleSalaryEditStartWrapper}
                handleSalarySaveWrapper={handleSalarySaveWrapper}
                handleSalaryKeyDownWrapper={handleSalaryKeyDownWrapper}
                handleSalaryInputChangeWrapper={handleSalaryInputChangeWrapper}
                handleExcitementChangeWrapper={handleExcitementChangeWrapper}
                handleDateChipsUpdateWrapper={handleDateChipsUpdateWrapper}
                handleAddToJobs={handleAddToJobsWrapper}
                handleRejectRelevant={handleRejectRelevantWrapper}
                handleStatusChange={handleStatusChangeWrapper}
                handleDeleteJob={handleDeleteJobWrapper}
                closeJobDropdownOpen={closeJobDropdownOpen}
                setCloseJobDropdownOpen={setCloseJobDropdownOpen}
                closeJobButtonRef={closeJobButtonRef}
                closeJobDropdownRef={closeJobDropdownRef}
                pipelineStatuses={pipelineStatuses}
                closeJobStatuses={closeJobStatuses}
                setPositionInputValue={setPositionInputValue}
                setCompanyInputValue={setCompanyInputValue}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-text-muted">Select a job to view details</div>
              </div>
            )}
          </div>
        </div>
        
        {/* Saved At Tooltip */}
        {showSavedAtTooltip && savedAtTooltipPos && savedAtTooltipText && (
          <div
            className="fixed z-[9999] bg-text-primary text-text-inverse text-xs rounded py-1.5 px-2.5 whitespace-nowrap shadow-lg pointer-events-none"
            style={{
              top: `${savedAtTooltipPos.top}px`,
              left: `${savedAtTooltipPos.left}px`,
              transform: 'translateY(-100%)',
              maxWidth: '90vw',
            }}
          >
            {savedAtTooltipText}
            <div className="absolute left-3 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-text-primary"></div>
          </div>
        )}
    </div>
  )
}

export default JobDetail
